import {gql} from "@apollo/client";
import {ApiService} from "./service";
import {
    getAssortmentsAndPopularProductsQuery_assortments,
    getAssortmentsAndPopularProductsQuery_assortments_searchProducts_products_SimpleProduct
} from "./__generated__/getAssortmentsAndPopularProductsQuery";
import {ProductStatus} from "../globalTypes";
import { OrderlySectorFragment } from "./__generated__/OrderlySectorFragment";
import { ProductFragment } from "./__generated__/ProductFragment";


export const ProductFragmentQL = gql`
fragment ProductFragment on Product {
    _id
    status
    texts { title description subtitle}
    media {
        file {
            url(version: "medium")
            original_url: url(version: "original") 
        }
    }
    tags
    assortmentPaths {
      links {
        assortmentId
      }
    }
    ... on SimpleProduct {
        baseUnit
        simulatedPrice {
            amount
            currencyCode
        }
        orderlyAvailability
        orderlyOptions {
            _id
            status
            texts { title }
            media { file { url } }
            ... on SimpleProduct {
                simulatedPrice {
                    amount currencyCode                
                }
                orderlyAvailability                        
            }
        }
    }
}
`;

const ASSORTMENTS_AND_POPULAR_QUERY = gql`
query getAssortmentsAndPopularProductsQuery {
    assortments(limit: 0) {
        _id
        texts {
            title
            subtitle        
        }    
        media {
            file {
                name
                url
            }
        }
        searchProducts(includeInactive: true) {
            products(limit: 0) {
                ...ProductFragment
            }
        }
    }
    products (tags: "popular") {
        ...ProductFragment
    }
}
${ProductFragmentQL}
`;

const SEARCH_PRODUCTS_QUERY = gql`
query search($term: String) {
    searchProducts(includeInactive: true, queryString: $term) {
        products {
            ...ProductFragment
        }
    }
}
${ProductFragmentQL}
`;

export interface ProductsAssortment {
    name: string;
    image: ProductImage;
    products: Product[];
}

export type ProductAssortments = ProductsAssortment[];

export interface Price {
    amount: number;
    currencyCode: string;
}

export enum ProductAvailability {
    AVAILABLE = "AVAILABLE",
    OUT_OF_STOCK = "OUT_OF_STOCK",
    DISABLED = "DISABLED",
}

export interface ProductData {
    id: string;
    isActive: boolean;
    title: string;
    description?: string;
    subtitle?: string;
    image?: ProductImage;
    price: Price;
    availability: ProductAvailability;
    tags: string[];
}

export interface Product extends ProductData {
    orderlyOptions: ProductData[];
}

export interface ProductImage {
    small_src?: string;
    medium_src?: string;
    src?: string;
}

export type SimpleProduct = getAssortmentsAndPopularProductsQuery_assortments_searchProducts_products_SimpleProduct;

const makeProductData = (data: SimpleProduct, isOption?: boolean) => {
    isOption = isOption || false;

    if (!data.simulatedPrice) {
        throw new Error('Product has no simulatedPrice');
    }
    if (!isOption && !(data as unknown as any).orderlyAvailability) {
        throw new Error('Product has no orderlyAvailability');
    }
    let image: ProductImage|undefined;
    if (data.media && data.media.length > 0 && data.media[0].file) {
        image = {
            src: data.media[0].file?.original_url ?? undefined,
            medium_src: data.media[0].file?.url ?? undefined,
            small_src: data.media[0].file?.url ?? undefined,
        }
    }
    return {
        id: data._id,
        isActive: data.status === "ACTIVE" && ((data as unknown as any).orderlyAvailability === ProductAvailability.AVAILABLE),
        title: data.texts!.title,
        description: data.texts!.description,
        tags: data.tags || [],
        image,
        price: data.simulatedPrice,
        subtitle: data.texts?.subtitle,
        availability: (data as unknown as any).orderlyAvailability
    } as ProductData;
};

const makeProduct = (data: SimpleProduct): Product|undefined => {
    if (data.status !== ProductStatus.ACTIVE) {
        return undefined;
    }
    // Active products without a simulated price are not orderable in the current
    // context (price simulation absent even though availability may be AVAILABLE).
    // This is an expected data condition, so skip them quietly instead of throwing
    // -- otherwise the throw below is logged via console.error and RUM reports it as
    // an exception ("Product has no simulatedPrice") on every product reload.
    if (!data.simulatedPrice) {
        return undefined;
    }
    try {
        return {
            ...makeProductData(data),
            // Drop options that have no price rather than letting one priceless
            // option throw and exclude the whole product.
            orderlyOptions: data.orderlyOptions
                .filter(d=>!!(d as SimpleProduct).simulatedPrice)
                .map(d=>makeProductData(d as SimpleProduct, true))
        } as Product;
    } catch (e) {
        // Unexpected exclusion (not the expected missing-price case handled above):
        // log at warn level so it is not captured as a production exception.
        console.warn('product excluded', {
            productId: data._id,
            reason: e instanceof Error ? e.message : String(e),
        });
        return undefined;
    }
};


export const searchProductsQ = async (term: string): Promise<Product[]> => {
    const resp = await ApiService.getClient()
        .query({query:SEARCH_PRODUCTS_QUERY, variables: {term}});
    return resp.data.searchProducts.products.map(makeProduct).filter((p: Product|undefined)=>!!p);
};


export const getProducts = async (sector: OrderlySectorFragment|undefined): Promise<{
    popularProducts: Product[],
    assortments: ProductAssortments
}> => {
    const resp = await ApiService.getClient()
        .query({query: ASSORTMENTS_AND_POPULAR_QUERY});

    let assortmentsSettings: AssortmentsSettings = {};
    try {
        const assortmentSettingsArr: AssortmentsSettings[] = JSON.parse(sector?.assortmentSettings)
        assortmentsSettings = assortmentSettingsArr.reduce((acc,v)=>({...acc,...v}), {})
    } catch (e) {}

    const popularProducts = resp.data.products
        .filter((pfrag:ProductFragment)=>getProductAssortments(pfrag).every(assortmentId=>isAssortmentAvailableBySettings(assortmentId, assortmentsSettings)))
        .map(makeProduct)
        .filter((p: Product|undefined)=>!!p);

    const assortments: ProductAssortments = resp.data.assortments
        .filter((data:getAssortmentsAndPopularProductsQuery_assortments)=>isAssortmentAvailableBySettings(data._id, assortmentsSettings))
        .map(
        (data: getAssortmentsAndPopularProductsQuery_assortments) => ({
            image: (data.media&&data.media.length>0)?{
                src: data.media[0].file?.url,
                medium_src: data.media[0].file?.url,
                small_src: data.media[0].file?.url,
            }:{src:null, medium_src:null, small_src:null},
            name: data.texts!.title,
            products: data.searchProducts.products.map(d=>makeProduct(d as SimpleProduct)).filter((p: Product|undefined)=>!!p)
        })
    );

    return {
        popularProducts, assortments
    }
};

const getProductAssortments = (product: ProductFragment):string[] => {
    return product.assortmentPaths.map(p=>p.links.map(l=>l.assortmentId)).reduce((acc,v)=>([...acc,...v]),[]);
}
type AssortmentsSettings = Record<string,{open:boolean,openTime:string,closeTime:string}>;
const isAssortmentAvailableBySettings = (assortmentId: string, assormentsSettings: AssortmentsSettings) => {
    try {
        const settings = assormentsSettings[assortmentId];
        if (!settings) {
            return true;
        }
        if (!settings.open) {
            return false;
        }
        const openMinutes = timeStringToMinutes(settings.openTime);
        const closeMinutes = timeStringToMinutes(settings.closeTime);
    
        const now = new Date();
        const minutesNow = now.getHours()*60 + now.getMinutes();
        return openMinutes <= minutesNow && minutesNow <= closeMinutes;
    } catch (e) {
        console.error("could not execute isAssortmentAvailableBySettings")
        console.error(e);
        return true
    }
}
const timeStringToMinutes = (timeString:string) => {
    const [hoursStr, minutesStr] = timeString.split(":");
    const hours = Number(hoursStr), minutes = Number(minutesStr);
    if (isNaN(hours) || isNaN(minutes)) return 0;
    return hours*60 + minutes;
}